/**
 * components/Drawer
 * ──────────────────────────────────────────────────────────────────────────
 * The ledger drawer: a FIXED OVERLAY PANEL, not a layout column.
 *
 * That distinction is the design's own, and it is load-bearing. An earlier
 * version of this drawer was an in-flow third column, which squeezed the
 * register beside it until its cells collided. As an overlay the table behind
 * it keeps its full width, and the scrim makes it obvious that the drawer is a
 * temporary surface over the page rather than part of it.
 *
 * Geometry: `top: 52px` (below the header) `right: 0; bottom: 0`, 520px wide
 * but never more than 92vw, with a `rgba(8,9,10,0.18)` scrim over the content
 * area that closes on press. Below 720px it takes the whole viewport — a
 * 520px panel on a phone is the whole screen anyway, minus a useless sliver.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Animated, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { neutral, scrim } from '../../theme/colors';
import { chrome } from '../../theme/spacing';
import { drawerShadow } from '../../theme/shadows';
import { duration, easing, offset } from '../../theme/motion';
import { useResponsive } from '../../lib/responsive';

export interface DrawerProps {
  visible: boolean;
  onClose: () => void;
  /** Pinned above the scroll area — the holder name and close button. */
  header: ReactNode;
  children: ReactNode;
}

export function Drawer({ visible, onClose, header, children }: DrawerProps) {
  const { width, fullScreenDrawer } = useResponsive();

  // `skslide`: 16px from the right, 200ms ease-out. One Animated.Value drives
  // both the panel's offset and the scrim's opacity so they cannot disagree.
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: visible ? 1 : 0,
      duration: duration.slide,
      easing,
      /*
       * There is no native animated module on web, so requesting the native
       * driver there logs a warning and silently falls back to JS. Ask for it
       * only where it exists.
       */
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [visible, progress]);

  if (!visible) return null;

  const panelWidth = fullScreenDrawer
    ? width
    : Math.min(chrome.drawerWidth, width * chrome.drawerMaxWidthRatio);

  return (
    <View
      // `pointerEvents` as a PROP is deprecated on react-native-web; it
      // belongs in the style object. `box-none` lets clicks through the
      // layer itself while the scrim and panel inside still receive them.
      style={[
        styles.layer,
        { top: fullScreenDrawer ? 0 : chrome.headerHeight, pointerEvents: 'box-none' },
      ]}
    >
      <Animated.View style={[StyleSheet.absoluteFill, { opacity: progress }]}>
        <Pressable
          style={styles.scrim}
          onPress={onClose}
          accessibilityLabel="Close the ledger"
        />
      </Animated.View>

      <Animated.View
        style={[
          styles.panel,
          drawerShadow,
          {
            width: panelWidth,
            opacity: progress,
            transform: [
              {
                translateX: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [offset.slideX, 0],
                }),
              },
            ],
          },
        ]}
      >
        <View style={styles.header}>{header}</View>
        <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
          {children}
        </ScrollView>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  layer: { position: 'absolute', left: 0, right: 0, bottom: 0 },
  scrim: { flex: 1, backgroundColor: scrim.drawer },
  panel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: neutral.surface,
    borderLeftWidth: 1,
    borderLeftColor: neutral.border,
  },
  header: {
    // 15px / 18px, from the design source.
    paddingVertical: 15,
    paddingHorizontal: 18,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  /*
   * No padding here on purpose. The drawer's body is a stack of full-bleed
   * sections, each with its own `16px 18px` padding and its own bottom rule —
   * padding the scroll container instead would inset those rules and they
   * would stop reaching the panel edges.
   */
  body: { paddingBottom: 24 },
});

export default Drawer;
